import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
  subDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useClassFiltersStore } from 'stores/classes';
import { MindbodyClass } from 'types/class'; // Assurez-vous que ce chemin d'importation est correct
import { getDayAbbreviation } from 'utils/functions';
import { ClassCard } from './components/Card';

type MobileClassesProps = {
  date: Date;
  classesData: MindbodyClass[];
  onPreviousDay: () => void;
  onNextDay: () => void;
};

const MobileClasses: React.FC<MobileClassesProps> = ({ date, classesData }) => {
  const courseRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const startOfCurrentWeek = startOfDay(startOfWeek(date, { weekStartsOn: 1 }));
  const endOfCurrentWeek = startOfDay(endOfWeek(date, { weekStartsOn: 1 }));

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const daysOfWeek = eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });

  const setDate = useClassFiltersStore((state) => state.setDate);
  const today = startOfDay(new Date());

  // État local pour le jour sélectionné
  const [selectedDay, setSelectedDay] = useState<Date>(today);

  useEffect(() => {
    // Met à jour le jour sélectionné en fonction de la semaine actuelle
    const startOfTheWeek = startOfWeek(date, { weekStartsOn: 1 });
    const endOfTheWeek = endOfWeek(date, { weekStartsOn: 1 });

    if (isToday(date) || (isAfter(date, startOfTheWeek) && isBefore(date, endOfTheWeek))) {
      setSelectedDay(date);
    } else {
      setSelectedDay(startOfTheWeek);
    }
  }, [date]);

  useEffect(() => {
    if (!isMobile) return;

    /*
    const now = new Date();

    const upcomingClass = classesData.find(
      (classData) =>
        new Date(classData.StartDateTime) > now && isSameDay(new Date(classData.StartDateTime), selectedDay),
    );


    if (upcomingClass && courseRefs.current[upcomingClass.Id]) {
      courseRefs.current[upcomingClass.Id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    */
  }, [isMobile, classesData, selectedDay]);

  const navigateDay = (offset: number) => {
    const newSelectedDay = addDays(selectedDay, offset);
    const startOfTheWeek = startOfWeek(selectedDay, { weekStartsOn: 1 });

    // Vérifie si le jour précédent est dans une nouvelle semaine
    if (offset < 0 && isSameDay(newSelectedDay, subDays(startOfTheWeek, 1))) {
      // Si le jour précédent est le dernier jour de la semaine précédente (Dimanche précédent)
      setPreviousWeek(); // Met à jour la semaine dans le store
      setSelectedDay(newSelectedDay); // Met à jour le jour sélectionné à dimanche
    } else if (offset > 0 && isSameDay(newSelectedDay, addDays(endOfWeek(selectedDay, { weekStartsOn: 1 }), 1))) {
      // Si le jour suivant est le premier jour de la nouvelle semaine (Lundi suivant)
      setNextWeek();
      setSelectedDay(newSelectedDay); // Met à jour le jour sélectionné à lundi
    } else {
      setSelectedDay(newSelectedDay); // Ajuste le jour sélectionné sans changer de semaine
    }

    setDate(newSelectedDay); // Synchronise la date globale avec le jour sélectionné
  };

  const setNextWeek = useClassFiltersStore((state) => state.setNextWeek);
  const setPreviousWeek = useClassFiltersStore((state) => state.setPreviousWeek);

  const handlePreviousDay = () => navigateDay(-1);
  const handleNextDay = () => navigateDay(1);

  // Filtrer les cours pour le jour sélectionné
  const classesForSelectedDay = classesData?.filter((classItem) =>
    isSameDay(new Date(classItem.StartDateTime), selectedDay),
  );

  const isSelected = (day: Date) => format(day, 'yyyy-MM-dd') === format(selectedDay, 'yyyy-MM-dd');

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="center" backgroundColor="#F5F5F5">
        {daysOfWeek.map((day, index) => (
          <Box
            key={index}
            p={2}
            borderWidth="0"
            borderRadius="0"
            bg={isSelected(day) ? '#D9D9D9' : 'transparent'}
            _hover={{ bg: '#D9D9D9', cursor: 'pointer' }}
            onClick={() => setSelectedDay(day)}
          >
            <Text textAlign="center" letterSpacing={0} fontSize="md" color="black" fontWeight="semibold">
              {getDayAbbreviation(day)}
            </Text>
            <Text textAlign="center" letterSpacing={0} fontSize="xs" fontWeight="semibold">
              {format(day, 'dd/MM', { locale: fr })}
            </Text>
          </Box>
        ))}
      </Flex>

      <VStack spacing={4}>
        {classesForSelectedDay.length === 0 ? (
          <Text>Pas de cours disponibles pour ce jour.</Text>
        ) : (
          classesForSelectedDay.map((course) => (
            <Box key={course.Id} w="full" ref={(el) => (courseRefs.current[course.Id] = el)}>
              <ClassCard mindbodyClass={course} />
            </Box>
          ))
        )}
      </VStack>
      {/* Boutons pour la navigation jour précédent / jour suivant */}
      <HStack height="69px" backgroundColor="#F5F5F5" justifyContent="space-between" w="full" alignItems={'center'}>
        <Text fontSize={13} fontWeight="bold" cursor="pointer" color="black" onClick={handlePreviousDay}>
          <ChevronLeftIcon /> jour précédent
        </Text>
        <Text fontSize={13} fontWeight="bold" cursor="pointer" color="black" onClick={handleNextDay}>
          jour suivant
          <ChevronRightIcon />
        </Text>
      </HStack>
    </VStack>
  );
};

export default MobileClasses;
